/* Rarely gift box — live 3D viewer controller.

   Wraps the CloudLab packing engine (the Packlane 3D previewer) so a page
   can show the real 10 x 7 x 5 mailer box, let the shopper turn it, and
   open or close it. The engine is heavy (~4.7 MB of script), so nothing is
   fetched until GiftBox3D.load() is called, and the page keeps showing its
   own placeholder until GiftBox3D reports ready.

   The engine hard-codes DOM ids (#renderCanvas, #loadingMask, …), reads a
   set of page globals (gift-box-3d-config.js), and resolves "#renderCanvas"
   the moment gift-box-3d-engine.js executes — so the host element must be
   in the document before the engine script is appended. The host is created
   once and MOVED between stage renders; the WebGL context survives a move.

   API
     GiftBox3D.supported()           -> bool (WebGL available)
     GiftBox3D.load(urls)            -> starts loading once; safe to call repeatedly
     GiftBox3D.mount(slotEl)         -> puts the canvas inside slotEl, resumes rendering
     GiftBox3D.VIEW                  -> the fixed camera angle (the box does not rotate)
     GiftBox3D.unmount()             -> parks the canvas off-DOM, pauses rendering
     GiftBox3D.setOpen(bool) / toggle()
     GiftBox3D.onReady(fn)
     GiftBox3D.state                 -> 'idle' | 'loading' | 'ready' | 'failed'
     GiftBox3D.open                  -> bool (current, or requested while loading)

   Events (dispatched on document, bubbling):
     gbx3d:ready, gbx3d:failed, gbx3d:tap, gbx3d:drag, gbx3d:open, gbx3d:close */
(function () {
  'use strict';
  if (window.GiftBox3D) return;

  var ORDER = ['jquery', 'pep', 'textures', 'config', 'babylon', 'rounded', 'engine', 'crinkle'];

  var G = {
    state: 'idle',
    open: false,
    host: null,
    canvas: null,
    slot: null,
    _cbs: [],
    _urls: null,
    _park: null,
    _ro: null,
    _busy: false
  };

  function emit(name, detail) {
    try { document.dispatchEvent(new CustomEvent(name, { bubbles: true, detail: detail || {} })); } catch (e) {}
  }

  G.supported = function () {
    try {
      var c = document.createElement('canvas');
      return !!(window.WebGLRenderingContext && (c.getContext('webgl') || c.getContext('experimental-webgl')));
    } catch (e) { return false; }
  };

  G.onReady = function (fn) {
    if (G.state === 'ready') { try { fn(); } catch (e) {} }
    else G._cbs.push(fn);
  };

  /* The engine's DOM contract, in one hidden host. The animation buttons are
     real because the engine binds its open/close sequences to their clicks. */
  function host() {
    if (G.host) return G.host;
    var h = document.createElement('div');
    h.className = 'gbx3d-host';
    h.innerHTML =
      '<canvas id="renderCanvas" class="gbx3d-canvas" touch-action="none" tabindex="0" ' +
        'aria-label="Rarely gift box. Tap to open or close it."></canvas>' +
      '<div id="loadingMask" hidden></div>' +
      '<div id="noWebGl" hidden></div>' +
      '<form id="modelSizeForm" hidden></form>' +
      '<div id="animationsActions" hidden>' +
        '<button type="button" id="animation_close" class="animation-btn active" tabindex="-1"></button>' +
        '<button type="button" id="animation_open" class="animation-btn" tabindex="-1"></button>' +
      '</div>';
    G.host = h;
    G.canvas = h.querySelector('canvas');
    /* the engine focuses the canvas on pointerdown; never let that scroll the page */
    G.canvas.focus = function () { try { HTMLElement.prototype.focus.call(this, { preventScroll: true }); } catch (e) {} };
    wireTap(G.canvas);
    return h;
  }

  /* Off-DOM parking spot that is still *in the document* (display:none
     would stall WebGL in some browsers; a 1px clipped box does not). */
  function park() {
    if (G._park) return G._park;
    var p = document.createElement('div');
    p.className = 'gbx3d-park';
    p.setAttribute('aria-hidden', 'true');
    p.style.cssText = 'position:fixed;left:-9999px;top:0;width:320px;height:320px;overflow:hidden;pointer-events:none;';
    document.body.appendChild(p);
    G._park = p;
    return p;
  }

  /* tap vs drag on the canvas: a short, still press toggles the lid */
  function wireTap(c) {
    var down = null;
    c.addEventListener('pointerdown', function (e) {
      down = { x: e.clientX, y: e.clientY, t: Date.now(), moved: false };
    });
    c.addEventListener('pointermove', function (e) {
      if (!down || down.moved) return;
      if (Math.abs(e.clientX - down.x) > 8 || Math.abs(e.clientY - down.y) > 8) {
        down.moved = true;
        emit('gbx3d:drag');
      }
    });
    function up(e) {
      if (!down) return;
      var d = down; down = null;
      if (!d.moved && Date.now() - d.t < 500) {
        emit('gbx3d:tap');
        G.toggle();
      }
    }
    c.addEventListener('pointerup', up);
    c.addEventListener('pointercancel', function () { down = null; });
    c.addEventListener('keydown', function (e) {
      if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); G.toggle(); }
    });
  }

  /* All scripts are appended at once with async=false: they download in
     parallel and execute in order (the engine needs jQuery, the textures,
     the config and Babylon before it runs). */
  function addScripts(srcs) {
    return new Promise(function (resolve, reject) {
      var left = srcs.length;
      if (!left) return resolve();
      srcs.forEach(function (src) {
        var s = document.createElement('script');
        s.src = src;
        s.async = false;
        s.onload = function () { if (--left === 0) resolve(); };
        s.onerror = function () { reject(new Error('gift-box-3d: failed to load ' + src)); };
        document.head.appendChild(s);
      });
    });
  }

  G.load = function (urls) {
    if (G.state !== 'idle') return;
    if (!G.supported()) { G.state = 'failed'; emit('gbx3d:failed', { reason: 'nowebgl' }); return; }
    G._urls = urls || window.GIFT_BOX3D_URLS;
    if (!G._urls) { G.state = 'failed'; emit('gbx3d:failed', { reason: 'nourls' }); return; }
    G.state = 'loading';
    /* the engine looks up #renderCanvas as it executes */
    var h = host();
    if (!h.parentNode) park().appendChild(h);

    window.GiftBox3DReady = function () {
      try { tune(); } catch (e) {}
      G.state = 'ready';
      if (G.slot) G.mount(G.slot);
      var cbs = G._cbs; G._cbs = [];
      cbs.forEach(function (fn) { try { fn(); } catch (e) {} });
      emit('gbx3d:ready');
      if (G.open) { G.open = false; G.setOpen(true); }
    };

    var srcs = [];
    ORDER.forEach(function (k) { if (G._urls[k]) srcs.push(G._urls[k]); });
    addScripts(srcs).then(function () {
      if (typeof window.GiftBox3DBoot !== 'function') throw new Error('gift-box-3d: engine boot hook missing');
      window.GiftBox3DBoot();
      /* if the engine never reports in, fall back to the placeholder */
      setTimeout(function () {
        if (G.state === 'loading') { G.state = 'failed'; emit('gbx3d:failed', { reason: 'timeout' }); }
      }, 45000);
    }).catch(function (err) {
      try { console.warn(err && err.message || err); } catch (e) {}
      G.state = 'failed';
      emit('gbx3d:failed', { reason: 'script' });
    });
  };

  function scene() { return window.scene || null; }
  function engine() { var s = scene(); return s ? s.getEngine() : null; }

  /* The fixed view: the composition of the original product photo — lid on
     top, long printed face to the right, short end to the left, seen from a
     little above. The lid hinges away from the viewer so the inside shows. */
  G.VIEW = { alpha: 1.2, beta: 1.05 };

  /* once the model is up: no camera input at all (the box does not turn;
     the only gesture is the tap), frame the box for the stage */
  function tune() {
    var s = scene(); if (!s) return;
    var cam = s.activeCamera; if (!cam) return;
    try { cam.inputs.clear(); } catch (e) {
      try { cam.detachControl(G.canvas); } catch (err) {}
    }
    try { cam.panningSensibility = 0; } catch (e) {}
    cam.alpha = G.VIEW.alpha; cam.beta = G.VIEW.beta;
    try { cam.lowerAlphaLimit = cam.upperAlphaLimit = G.VIEW.alpha; cam.lowerBetaLimit = cam.upperBetaLimit = G.VIEW.beta; } catch (e) {}
    G.radius0 = cam.radius;
    G.frame();
  }

  /* Frame the box for the stage. The engine frames for a wide editor canvas
     and leaves the closed box small; this pulls in close enough that the
     closed box fills the middle of the stage and the open lid still clears
     the top. The slot keeps a fixed aspect, so one setting serves all sizes. */
  G.frame = function () {
    var s = scene(); if (!s || !s.activeCamera || !G.radius0) return;
    var cam = s.activeCamera;
    /* the canvas is 1.2x taller than the box area (see .gfx-box3d): a
       vertical FOV means the box would grow with it, so back off the radius
       by the same ratio and aim a little higher so the box sits where it did */
    cam.radius = G.radius0 * 0.53;
    if (!G._targetSet) {
      /* up a little so the box sits mid-stage (the open lid may run behind
         the headline; that is fine), and nudged along the camera's right
         vector so the box is centred horizontally in the visible stage */
      cam.target.y += 32;
      var a = G.VIEW.alpha, d = 40;
      cam.target.x -= Math.sin(a) * d; cam.target.z += Math.cos(a) * d;
      G._targetSet = true;
    }
    try { cam.lowerRadiusLimit = cam.radius; cam.upperRadiusLimit = cam.radius; } catch (e) {}
  };

  function resize() {
    var e = engine(); if (!e) return;
    try { e.resize(); } catch (err) {}
    G.frame();
  }

  function run() {
    var s = scene(), e = engine(); if (!s || !e) return;
    e.stopRenderLoop();
    e.runRenderLoop(function () { s.render(); });
  }

  G.mount = function (slot) {
    if (!slot) return;
    G.slot = slot;
    var h = host();
    if (h.parentNode !== slot) slot.appendChild(h);
    if (G.state !== 'ready') return;
    run();
    requestAnimationFrame(resize);
    setTimeout(resize, 60);
    if (!G._ro && window.ResizeObserver) {
      G._ro = new ResizeObserver(function () { if (G.host && G.host.parentNode === G.slot) resize(); });
    }
    if (G._ro) { try { G._ro.disconnect(); G._ro.observe(slot); } catch (e) {} }
  };

  G.unmount = function () {
    var h = G.host; if (!h) return;
    if (G.slot && G._ro) { try { G._ro.disconnect(); } catch (e) {} }
    G.slot = null;
    park().appendChild(h);
    var e = engine(); if (e) { try { e.stopRenderLoop(); } catch (err) {} }
  };

  G.setOpen = function (open) {
    open = !!open;
    if (G.state !== 'ready') { G.open = open; return; }
    if (G.open === open) return;
    var btn = document.getElementById(open ? 'animation_open' : 'animation_close');
    if (!btn) return;
    G.open = open;
    btn.click();
    emit(open ? 'gbx3d:open' : 'gbx3d:close');
  };

  G.toggle = function () { G.setOpen(!G.open); };

  window.GiftBox3D = G;
})();
